import { Observable, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import type { Model } from "applesauce-core/event-store";
import { RelayGroup } from "applesauce-relay";
import type { Relay } from "applesauce-relay";
import type { NostrEvent } from "nostr-tools";
import {
  REPO_KIND,
  getRepoRelays,
  getRepoMaintainers,
  resolveChain,
} from "@/lib/nip34";
import { pool } from "@/services/nostr";
import { normalizeUrl } from "@/lib/url";

/**
 * Return a reactive Observable<string[]> of normalized relay URLs for a
 * RelayGroup. Emits whenever the group gains or loses relays.
 *
 * relays$ is protected in TypeScript but public at runtime — we cast to
 * access it so we can react to relay additions without polling.
 *
 * Returns an observable of [] when group is undefined.
 */
export function relayGroupUrls$(
  group: RelayGroup | undefined,
): Observable<string[]> {
  if (!group) return new Observable((s) => s.next([]));
  return (group as unknown as { relays$: Observable<Relay[]> }).relays$.pipe(
    map((relays) => relays.map((r) => normalizeUrl(r.url))),
  );
}

/**
 * RepositoryRelayGroup — a long-lived RelayGroup for a repository, cached by
 * the EventStore model system alongside RepositoryModel.
 *
 * Starts with an empty group and grows as the repository's declared relay list
 * is discovered from announcement events (kind 30617). Once the confirmed
 * chain resolves private, the group is pruned to the relay union from confirmed
 * announcements. This prevents URL hints or an unconfirmed maintainer from
 * retaining a broad relay in a private repository's read path.
 *
 * Because RelayGroup.add() is idempotent (checks has() before next()) and
 * internalSubscription uses a WeakMap cache keyed on the Relay instance,
 * adding a relay that is already in the group is a no-op, and adding a new
 * relay opens a subscription only to that relay — existing subscriptions are
 * untouched.
 *
 * Model cache key: (selectedMaintainer, dTag) — same as RepositoryModel, so
 * the two models share a lifetime and are torn down together.
 */
export function RepositoryRelayGroup(
  selectedMaintainer: string,
  dTag: string,
): Model<RelayGroup> {
  return (store) => {
    const group = new RelayGroup([]);

    return new Observable<RelayGroup>((observer) => {
      const subs = new Subscription();

      // Subscribe to every kind:30617 event for this dTag in the store.
      // store.addressable() re-emits whenever the event changes (new version
      // of a replaceable event). We watch all pubkeys we discover via BFS,
      // mirroring what RepositoryModel does — but here we only care about
      // the relay tags, not the full chain resolution.
      const subscribed = new Set<string>();

      function subscribe(pubkey: string) {
        if (subscribed.has(pubkey)) return;
        subscribed.add(pubkey);

        subs.add(
          store
            .addressable({ kind: REPO_KIND, pubkey, identifier: dTag })
            .subscribe((ev) => {
              if (!ev) return;

              const announcements = store.getByFilters([
                {
                  kinds: [REPO_KIND],
                  "#d": [dTag],
                },
              ]) as NostrEvent[];
              const resolved = resolveChain(
                announcements,
                selectedMaintainer,
                dTag,
              );

              // Public repositories retain the established additive discovery
              // behaviour. Private repositories accept relay URLs only from
              // the recursively confirmed announcement set.
              const relayUrls = resolved?.isPrivate
                ? resolved.relays
                : getRepoRelays(ev);
              const allowed = new Set(relayUrls.map(normalizeUrl));
              if (resolved?.isPrivate) {
                for (const relay of [...group.relays]) {
                  if (!allowed.has(normalizeUrl(relay.url))) {
                    group.remove(relay);
                  }
                }
              }
              for (const url of relayUrls) {
                const relay = pool.relay(normalizeUrl(url));
                if (!group.has(relay)) group.add(relay);
              }

              // Follow the maintainers tag to discover co-maintainer relays
              for (const mp of getRepoMaintainers(ev)) {
                subscribe(mp);
              }

              // Emit the group (same instance) so subscribers know it grew
              observer.next(group);
            }),
        );
      }

      subscribe(selectedMaintainer);

      // Emit immediately so consumers get the group reference synchronously
      observer.next(group);

      return () => {
        subs.unsubscribe();
      };
    });
  };
}
